/**
 * iter214 Phase 2 — WhatsApp Disputes card.
 *
 * Shows open + resolved disputes raised by members tapping WhatsApp template
 * quick-reply buttons (`⚠️ Dispute` on balance reminders, `⚠️ Appeal` on
 * fine notices). Admin can resolve a dispute with a note, or reopen later.
 *
 * Used in two places:
 *   • WhatsAppSettingsPage (super-admin) — global via GET /whatsapp/disputes
 *   • GroupDetailPage > Overview tab (group admin) — scoped via
 *     GET /whatsapp/groups/{id}/disputes
 *
 * Props:
 *   apiPath:         backend path to fetch
 *   title:           card heading
 *   refreshInterval: ms (default 30_000)
 *   defaultLimit:    rows (default 50)
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { RefreshCw, Loader2, AlertTriangle, CheckCircle2, RotateCcw, Gavel, DollarSign, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const KIND_META = {
  balance: { label: 'Balance dispute', icon: DollarSign, color: 'text-amber-600 dark:text-amber-400' },
  fine: { label: 'Fine appeal', icon: Gavel, color: 'text-red-600 dark:text-red-400' },
  other: { label: 'Other', icon: AlertTriangle, color: 'text-muted-foreground' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const WhatsAppDisputesCard = ({
  apiPath,
  title = 'WhatsApp Disputes',
  refreshInterval = 30000,
  defaultLimit = 50,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(defaultLimit) });
      if (!showResolved) params.set('status', 'open');
      const res = await api.get(`${apiPath}?${params.toString()}`);
      setRows(res.data?.entries || []);
    } catch {
      /* soft fail */
    } finally {
      setLoading(false);
    }
  }, [apiPath, defaultLimit, showResolved]);

  useEffect(() => {
    fetchDisputes();
    const t = setInterval(fetchDisputes, refreshInterval);
    return () => clearInterval(t);
  }, [fetchDisputes, refreshInterval]);

  const resolveOne = async (row) => {
    const note = window.prompt(
      `Resolution note for ${row.from_user_name}'s ${row.kind} dispute (optional):`,
      '',
    );
    if (note === null) return;
    setUpdatingId(row.id);
    try {
      await api.post(`/whatsapp/disputes/${row.id}/resolve`, { resolution_notes: note || null });
      toast.success('Dispute resolved');
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resolve failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const reopenOne = async (row) => {
    if (!window.confirm(`Reopen ${row.from_user_name}'s ${row.kind} dispute?`)) return;
    setUpdatingId(row.id);
    try {
      await api.post(`/whatsapp/disputes/${row.id}/reopen`);
      toast.success('Dispute reopened');
      fetchDisputes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reopen failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const openCount = rows.filter((r) => r.status === 'open').length;

  return (
    <Card className="p-4 sm:p-5 rounded-2xl border-2 space-y-3" data-testid="whatsapp-disputes-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <h3 className="text-sm font-bold uppercase tracking-wide truncate">{title}</h3>
          {openCount > 0 && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-300" data-testid="disputes-open-count">
              {openCount} OPEN
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant={showResolved ? 'outline' : 'default'}
            onClick={() => setShowResolved(!showResolved)}
            data-testid="disputes-status-toggle"
            className="rounded-full h-8 text-xs"
          >
            {showResolved ? 'All' : 'Open only'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchDisputes}
            disabled={loading}
            data-testid="disputes-refresh-btn"
            className="rounded-full h-8 text-xs"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="disputes-rows-container">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-xs" data-testid="disputes-empty-state">
            <CheckCircle2 className="w-6 h-6 mx-auto opacity-30 mb-2" />
            <p>No {showResolved ? '' : 'open '}disputes.</p>
            <p className="text-xs mt-1">Members can raise disputes by tapping <strong>⚠️ Dispute</strong> on a WhatsApp balance reminder or <strong>⚠️ Appeal</strong> on a fine notice.</p>
          </div>
        ) : (
          rows.map((row) => {
            const meta = KIND_META[row.kind] || KIND_META.other;
            const Icon = meta.icon;
            const resolved = row.status === 'resolved';
            return (
              <div
                key={row.id}
                className={`rounded-xl border-2 p-3 space-y-1.5 ${resolved ? 'opacity-60 bg-muted/20' : 'bg-card'}`}
                data-testid={`dispute-row-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${meta.color}`} />
                    <p className="text-xs font-semibold truncate">
                      {row.from_user_name || 'Unknown'}
                      <span className={`text-xs font-bold uppercase ml-1.5 ${meta.color}`}>
                        {meta.label}
                      </span>
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {relativeTime(row.created_at)}
                  </span>
                </div>

                <p className="text-xs text-muted-foreground">
                  Triggered by template <code className="font-mono text-xs">{row.original_template || row.original_notif_type || '?'}</code>
                </p>

                {row.notes && (
                  <p className="text-xs italic bg-muted/30 rounded px-2 py-1">
                    Member&apos;s button tap: &quot;{row.notes}&quot;
                  </p>
                )}

                {/* iter260 — member's typed appeal reason */}
                {row.appeal_reason && (
                  <div className="text-xs bg-amber-50 dark:bg-amber-500/10 border border-amber-300/40 dark:border-amber-500/30 rounded px-2 py-1" data-testid={`dispute-appeal-reason-${row.id}`}>
                    <p className="font-semibold text-amber-700 dark:text-amber-300 text-xs uppercase tracking-wide">Member&apos;s reason</p>
                    <p className="text-amber-800 dark:text-amber-200 mt-0.5">&quot;{row.appeal_reason}&quot;</p>
                  </div>
                )}

                {resolved && (
                  <div className="text-xs bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300/40 dark:border-emerald-500/30 rounded px-2 py-1">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-300">
                      ✓ Resolved {relativeTime(row.resolved_at)}
                    </p>
                    {row.resolution_notes && (
                      <p className="text-emerald-700/80 dark:text-emerald-300/80 mt-0.5">
                        {row.resolution_notes}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end gap-1.5 pt-0.5">
                  {resolved ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => reopenOne(row)}
                      disabled={updatingId === row.id}
                      data-testid={`dispute-reopen-${row.id}`}
                      className="h-7 text-xs rounded-full px-2"
                    >
                      {updatingId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      <span className="ml-1">Reopen</span>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => resolveOne(row)}
                      disabled={updatingId === row.id}
                      data-testid={`dispute-resolve-${row.id}`}
                      className="h-7 text-xs rounded-full px-2.5 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {updatingId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      <span className="ml-1">Resolve</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};

export default WhatsAppDisputesCard;
